import { normalizeMarks, normalizeParagraphs } from "./richtext";
import { QR_MAX_LENGTH, type SceneDocument, TEXT_MAX_LENGTH, type TextElement } from "./schema";

/**
 * Find & replace in documents (editor ⌘F). Texts keep their formatting: the
 * plain `text` changes, marks are shifted/split around the replaced ranges
 * (a replacement takes the formatting of the first character it replaces)
 * and paragraph formats follow their lines.
 */

export interface TextRange {
  from: number;
  to: number;
}

export interface FindOptions {
  caseSensitive?: boolean;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Non-overlapping occurrences of `query` in `text`, in order. */
export function findTextMatches(
  text: string,
  query: string,
  options: FindOptions = {},
): TextRange[] {
  if (!query) return [];
  const pattern = new RegExp(escapeRegExp(query), options.caseSensitive ? "g" : "gi");
  const out: TextRange[] = [];
  for (const match of text.matchAll(pattern)) {
    if (match[0].length === 0) continue;
    out.push({ from: match.index, to: match.index + match[0].length });
  }
  return out;
}

type RichText = Pick<TextElement, "text" | "marks" | "paragraphs">;

/**
 * Replaces ranges of a text (sorted or not, must not overlap) by `replacement`.
 * Returns the new text with marks and paragraph formats adjusted.
 */
export function replaceTextRanges(
  source: RichText,
  ranges: readonly TextRange[],
  replacement: string,
): RichText {
  const text = source.text;
  const sorted = [...ranges]
    .map((range) => ({
      from: Math.max(0, Math.min(text.length, range.from)),
      to: Math.max(0, Math.min(text.length, range.to)),
    }))
    .filter((range) => range.to >= range.from)
    .sort((a, b) => a.from - b.from);
  const marks = normalizeMarks(source.marks, text.length);
  // Mark index per character of the old text (-1: unformatted).
  const markAt = new Int32Array(text.length).fill(-1);
  marks.forEach((mark, index) => {
    for (let offset = mark.from; offset < mark.to; offset++) markAt[offset] = index;
  });

  let out = "";
  const styles: number[] = [];
  /** Offset in the old text each new character comes from (paragraph formats). */
  const origin: number[] = [];
  let cursor = 0;
  for (const range of sorted) {
    if (range.from < cursor) continue; // overlapping range: skip
    for (let offset = cursor; offset < range.from; offset++) {
      styles.push(markAt[offset]!);
      origin.push(offset);
    }
    out += text.slice(cursor, range.from);
    const style =
      range.to > range.from
        ? markAt[range.from]!
        : range.from > 0
          ? markAt[range.from - 1]!
          : (markAt[0] ?? -1);
    for (let index = 0; index < replacement.length; index++) {
      styles.push(style);
      origin.push(range.from);
    }
    out += replacement;
    cursor = range.to;
  }
  for (let offset = cursor; offset < text.length; offset++) {
    styles.push(markAt[offset]!);
    origin.push(offset);
  }
  out += text.slice(cursor);

  const result: RichText = { text: out };
  // Marks: runs of characters coming from the same mark.
  if (marks.length > 0) {
    const next: NonNullable<TextElement["marks"]> = [];
    let start = 0;
    for (let index = 1; index <= styles.length; index++) {
      if (index < styles.length && styles[index] === styles[start]) continue;
      const markIndex = styles[start]!;
      if (markIndex >= 0) {
        const { from: _from, to: _to, ...style } = marks[markIndex]!;
        next.push({ from: start, to: index, ...style });
      }
      start = index;
    }
    const normalized = normalizeMarks(next, out.length);
    if (normalized.length > 0) result.marks = normalized;
  }
  // Paragraph formats: each new line takes the format of the old line it starts in.
  if (source.paragraphs) {
    const oldLineStarts = [0];
    for (let offset = 0; offset < text.length; offset++) {
      if (text[offset] === "\n") oldLineStarts.push(offset + 1);
    }
    const oldLineOf = (offset: number) => {
      let line = 0;
      while (line + 1 < oldLineStarts.length && oldLineStarts[line + 1]! <= offset) line++;
      return line;
    };
    const formats: (NonNullable<TextElement["paragraphs"]>[number] | null)[] = [];
    const lineStart = (position: number) => {
      const old = position < origin.length ? origin[position]! : text.length;
      formats.push(source.paragraphs?.[oldLineOf(old)] ?? null);
    };
    lineStart(0);
    for (let offset = 0; offset < out.length; offset++) {
      if (out[offset] === "\n") lineStart(offset + 1);
    }
    const normalized = normalizeParagraphs(formats, formats.length);
    if (normalized) result.paragraphs = normalized;
  }
  return result;
}

export type SearchField = "text" | "qr";

export interface DocumentMatch {
  pageIndex: number;
  elementId: string;
  field: SearchField;
  from: number;
  to: number;
}

/** Searchable elements of a page in reading order (groups included). */
function pageElements(doc: SceneDocument, pageIndex: number): string[] {
  const out: string[] = [];
  const visit = (id: string) => {
    const element = doc.elements[id];
    if (!element) return;
    if (element.type === "group") for (const child of element.children) visit(child);
    else if (element.type === "text" || element.type === "qr") out.push(id);
  };
  for (const id of doc.pages[pageIndex]?.children ?? []) visit(id);
  return out;
}

/** All matches in the document, page by page. */
export function findInDocument(
  doc: SceneDocument,
  query: string,
  options: FindOptions & { qr?: boolean } = {},
): DocumentMatch[] {
  if (!query) return [];
  const out: DocumentMatch[] = [];
  doc.pages.forEach((_page, pageIndex) => {
    for (const elementId of pageElements(doc, pageIndex)) {
      const element = doc.elements[elementId];
      if (!element) continue;
      if (element.type === "text") {
        for (const range of findTextMatches(element.text, query, options)) {
          out.push({ pageIndex, elementId, field: "text", ...range });
        }
      } else if (element.type === "qr" && options.qr !== false) {
        for (const range of findTextMatches(element.value, query, options)) {
          out.push({ pageIndex, elementId, field: "qr", ...range });
        }
      }
    }
  });
  return out;
}

/**
 * Replaces matches in a (draft) document. Matches of one element are replaced
 * together; returns the number of replaced occurrences. Replacements that would
 * exceed the length limits are skipped for that element.
 */
export function replaceMatches(
  doc: SceneDocument,
  matches: readonly DocumentMatch[],
  replacement: string,
): number {
  const byElement = new Map<string, DocumentMatch[]>();
  for (const match of matches) {
    const list = byElement.get(match.elementId) ?? [];
    list.push(match);
    byElement.set(match.elementId, list);
  }
  let count = 0;
  for (const [elementId, list] of byElement) {
    const element = doc.elements[elementId];
    if (!element) continue;
    if (element.type === "text") {
      const next = replaceTextRanges(element, list, replacement);
      if (next.text.length > TEXT_MAX_LENGTH) continue;
      element.text = next.text;
      if (next.marks) element.marks = next.marks;
      else delete element.marks;
      if (next.paragraphs) element.paragraphs = next.paragraphs;
      else delete element.paragraphs;
      count += list.length;
    } else if (element.type === "qr") {
      const next = replaceTextRanges({ text: element.value }, list, replacement).text;
      if (next.length === 0 || next.length > QR_MAX_LENGTH) continue;
      element.value = next;
      count += list.length;
    }
  }
  return count;
}
